import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Grain, Thoranam } from '../components/Thoranam';
import { NavBar } from '../components/NavBar';
import { OrnateFrame } from '../components/OrnateFrame';
import { Lightbox } from '../components/Lightbox';
import { loadAlbum } from '../lib/album';
import { PAGE_SIZE, clampPage, pageCount } from '../lib/ids';
import type { Album, Photo } from '../types';

export function AlbumPage() {
  const [params, setParams] = useSearchParams();
  const [album, setAlbum] = useState<Album | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    loadAlbum()
      .then((data) => {
        if (!alive) return;
        if (!data) {
          setError('The catalogue has not been inscribed yet.');
          return;
        }
        setAlbum(data.album);
        setPhotos(data.photos);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Could not open the album.');
      });
    return () => {
      alive = false;
    };
  }, []);

  const totalPages = pageCount(photos.length || 1, PAGE_SIZE);
  const page = clampPage(Number(params.get('p') ?? 1), photos.length || 1, PAGE_SIZE);
  const slice = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return photos.slice(start, start + PAGE_SIZE);
  }, [page, photos]);
  const openIndex = openId ? photos.findIndex((p) => p.id === openId) : -1;

  function go(next: number) {
    const p = clampPage(next, photos.length, PAGE_SIZE);
    setParams(p === 1 ? {} : { p: String(p) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="shell">
      <div className="bg bg-album" />
      <Thoranam />
      <Grain />
      <NavBar />
      <main className="page">
        <header className="page-head">
          <p className="kicker">The leafed book</p>
          <h1>{album ? `${album.title} of ${album.honoree}` : 'The album'}</h1>
          <p>
            {photos.length
              ? `${photos.length} photographs · page ${page} of ${totalPages}`
              : 'Waiting for the household to bind the leaves'}
          </p>
        </header>
        {error ? <p className="msg">{error}</p> : null}
        {photos.length ? (
          <>
            <div className="pager">
              <button className="btn" type="button" disabled={page <= 1} onClick={() => go(page - 1)}>
                Previous leaf
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                className="btn"
                type="button"
                disabled={page >= totalPages}
                onClick={() => go(page + 1)}
              >
                Next leaf
              </button>
            </div>
            <div className="masonry">
              {slice.map((photo) => (
                <OrnateFrame key={photo.id} photo={photo} onOpen={setOpenId} />
              ))}
            </div>
          </>
        ) : null}
      </main>
      {openIndex >= 0 ? (
        <Lightbox
          photos={photos}
          index={openIndex}
          onClose={() => setOpenId(null)}
          onIndex={(i) => setOpenId(photos[i]?.id ?? null)}
        />
      ) : null}
    </div>
  );
}
